import {
    FEATURE_AVAILABILITY,
    FEATURE_CONTENT_VISIBILITY,
    FEATURE_ACTION_MODE,
    FEATURE_CREDIT_ACTION,
    FEATURE_CREDIT_RULE,
    FEATURE_REASON_CODE,
    createFeatureAccessDecision,
    getCreditActionByRule,
    getFeatureCreditCost,
    isFeatureUnlocked
} from '@/core/credit/types';
import {
    FALLBACK_TIER_ID,
    getFeaturePolicy
} from '@/config/featurePolicyConfig';

function toSafeCreditsTotal(creditsTotal) {
    return Number.isFinite(Number(creditsTotal)) ? Number(creditsTotal) : 0;
}

function normalizeTierId(tierId) {
    const normalized = String(tierId || FALLBACK_TIER_ID).trim().toLowerCase();
    return normalized || FALLBACK_TIER_ID;
}

function resolveTierAvailabilityByState(tierAvailabilityState) {
    if (tierAvailabilityState === 'unavailable') {
        return false;
    }
    if (tierAvailabilityState === 'partial' || tierAvailabilityState === 'full') {
        return true;
    }
    return null;
}

export function resolveEffectiveTierRule(policy, tierId) {
    const tierAvailabilityState = policy?.tierAvailabilityStateByTier?.[tierId];
    const availabilityByState = resolveTierAvailabilityByState(tierAvailabilityState);
    const tierAvailable = availabilityByState === null
        ? Boolean(policy?.tierAvailability?.[tierId])
        : availabilityByState;

    const tierRule = policy?.creditRuleByTier?.[tierId];
    const normalizedTierRule = Object.values(FEATURE_CREDIT_RULE).includes(tierRule)
        ? tierRule
        : null;
    const creditRule = normalizedTierRule || policy?.creditRule || FEATURE_CREDIT_RULE.NONE;

    const hasTierConfirmRule = Object.prototype.hasOwnProperty.call(policy?.confirmRequiredByTier || {}, tierId);
    const confirmRequired = hasTierConfirmRule
        ? Boolean(policy?.confirmRequiredByTier?.[tierId])
        : Boolean(policy?.confirmRequired);

    return {
        tierAvailable,
        creditRule,
        confirmRequired
    };
}

function hasEnoughCredits(requiredCredits, creditsTotal) {
    if (requiredCredits <= 0) {
        return true;
    }
    return creditsTotal >= requiredCredits;
}

function resolveLockedOrInsufficientDecision({ requiredCredits, creditsTotal, creditAction, reasonCodeLocked, reasonCodeInsufficient }) {
    if (hasEnoughCredits(requiredCredits, creditsTotal)) {
        return createFeatureAccessDecision({
            contentVisibility: FEATURE_CONTENT_VISIBILITY.RESTRICTED,
            availability: FEATURE_AVAILABILITY.LOCKED,
            actionMode: FEATURE_ACTION_MODE.NEED_CONFIRM,
            creditAction,
            reasonCode: reasonCodeLocked
        });
    }

    return createFeatureAccessDecision({
        contentVisibility: FEATURE_CONTENT_VISIBILITY.RESTRICTED,
        availability: FEATURE_AVAILABILITY.INSUFFICIENT_CREDITS,
        actionMode: FEATURE_ACTION_MODE.BLOCKED,
        creditAction,
        reasonCode: reasonCodeInsufficient
    });
}

export function resolveFeatureAccess({
    accountState,
    imageUnlockState,
    sessionState,
    policy
}) {
    // 校验策略配置必须存在
    if (!policy || typeof policy !== 'object') {
        throw new Error('[featureAccessResolver] policy is required');
    }

    // 提取用户账户状态：tier等级和积分总额
    const tierId = normalizeTierId(accountState?.tierId || FALLBACK_TIER_ID);
    const creditsTotal = toSafeCreditsTotal(accountState?.creditsTotal);
    const tierRule = resolveEffectiveTierRule(policy, tierId);

    // 【第一层判定】检查用户tier是否有权使用该功能
    // 若策略中未对当前tier开启可用性，则返回不可用或需升级
    if (!tierRule.tierAvailable) {
        const actionMode = policy.purchasable === false
            ? FEATURE_ACTION_MODE.BLOCKED
            : FEATURE_ACTION_MODE.UPGRADE_ONLY;
        return createFeatureAccessDecision({
            contentVisibility: FEATURE_CONTENT_VISIBILITY.RESTRICTED,
            availability: FEATURE_AVAILABILITY.UNAVAILABLE_TIER,
            actionMode,
            creditAction: FEATURE_CREDIT_ACTION.NONE,
            reasonCode: FEATURE_REASON_CODE.UNAVAILABLE_FOR_TIER
        });
    }

    // 提取策略中的积分规则和相关计算
    const creditRule = tierRule.creditRule || FEATURE_CREDIT_RULE.NONE;
    const creditAction = getCreditActionByRule(creditRule);
    const requiredCredits = getFeatureCreditCost(policy, tierId);
    const inflightActionKey = `${policy.featureCode || ''}::${policy.subFeatureCode || ''}`;
    const isInflight = Boolean(sessionState?.inflightActionMap?.[inflightActionKey]);

    // 【第二层判定】无需积分扣费的功能直接开放
    if (creditRule === FEATURE_CREDIT_RULE.NONE) {
        return createFeatureAccessDecision({
            contentVisibility: FEATURE_CONTENT_VISIBILITY.FULL,
            availability: FEATURE_AVAILABILITY.AVAILABLE,
            actionMode: FEATURE_ACTION_MODE.DIRECT,
            creditAction: FEATURE_CREDIT_ACTION.NONE,
            reasonCode: FEATURE_REASON_CODE.AVAILABLE_NO_CHARGE
        });
    }

    // 【第三层判定】默认扣费规则：检查积分余额是否足够
    // 若不足则阻断，足够则直接扣费放行
    if (creditRule === FEATURE_CREDIT_RULE.DEFAULT) {
        if (!hasEnoughCredits(requiredCredits, creditsTotal)) {
            return createFeatureAccessDecision({
                contentVisibility: FEATURE_CONTENT_VISIBILITY.RESTRICTED,
                availability: FEATURE_AVAILABILITY.INSUFFICIENT_CREDITS,
                actionMode: FEATURE_ACTION_MODE.BLOCKED,
                creditAction,
                reasonCode: FEATURE_REASON_CODE.INSUFFICIENT_CREDITS_DEFAULT
            });
        }

        return createFeatureAccessDecision({
            contentVisibility: FEATURE_CONTENT_VISIBILITY.FULL,
            availability: FEATURE_AVAILABILITY.AVAILABLE,
            actionMode: FEATURE_ACTION_MODE.DIRECT,
            creditAction,
            reasonCode: FEATURE_REASON_CODE.AVAILABLE_DEFAULT_CHARGE
        });
    }

    // 【第四层判定】CONFIRM模式：检查当前图片该功能是否已解锁
    // 已解锁则免积分直接可用（基于imageUnlockState从内存/localStorage恢复）
    const unlocked = isFeatureUnlocked(imageUnlockState, policy.featureCode, policy.subFeatureCode);
    if (unlocked) {
        return createFeatureAccessDecision({
            contentVisibility: FEATURE_CONTENT_VISIBILITY.FULL,
            availability: FEATURE_AVAILABILITY.AVAILABLE,
            actionMode: FEATURE_ACTION_MODE.DIRECT,
            creditAction: FEATURE_CREDIT_ACTION.NONE,
            reasonCode: FEATURE_REASON_CODE.AVAILABLE_UNLOCKED_IMAGE
        });
    }

    // 【第五层判定】检查是否有正在进行的解锁操作（防重复点击）
    if (isInflight) {
        return createFeatureAccessDecision({
            contentVisibility: FEATURE_CONTENT_VISIBILITY.RESTRICTED,
            availability: FEATURE_AVAILABILITY.LOCKED,
            actionMode: FEATURE_ACTION_MODE.BLOCKED,
            creditAction,
            reasonCode: FEATURE_REASON_CODE.LOCKED_CONFIRM_REQUIRED
        });
    }

    // 【最终判定】未解锁且无进行中的操作：检查积分是否足够进行确认扣费
    // 积分足够显示确认按钮，不足则显示积分不足阻断
    return resolveLockedOrInsufficientDecision({
        requiredCredits,
        creditsTotal,
        creditAction,
        reasonCodeLocked: FEATURE_REASON_CODE.LOCKED_CONFIRM_REQUIRED,
        reasonCodeInsufficient: FEATURE_REASON_CODE.INSUFFICIENT_CREDITS_CONFIRM
    });
}

export function resolveFeatureAccessByCode({
    accountState,
    imageUnlockState,
    sessionState,
    policySnapshot,
    featureCode,
    subFeatureCode
}) {
    const policy = getFeaturePolicy(policySnapshot, featureCode, subFeatureCode);
    return resolveFeatureAccess({
        accountState,
        imageUnlockState,
        sessionState,
        policy
    });
}
